import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastProvider } from './components/Common/AdvancedToast';
import { ConnectionStatus } from './components/Common/LoadingStates';
import { DevelopmentPanel } from './utils/developmentTools';
import { ErrorBoundary } from './components/Common/ErrorBoundary';
import { PerformanceMonitor } from './utils/performance';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { ProfileSelectionPage } from './pages/ProfileSelectionPage';
import { DashboardPage } from './pages/DashboardPage';
import { ProjectsPage } from './pages/ProjectsPage';
import { ProjectWizardIntroPage } from './pages/ProjectWizardIntroPage';
import { ProjectWizardStepPage } from './pages/ProjectWizardStepPage';
import { ProjectWizardDocumentsPage } from './pages/ProjectWizardDocumentsPage';
import { ProjectWizardReviewPage } from './pages/ProjectWizardReviewPage';
import { ProjectDetailPage } from './pages/ProjectDetailPage';
import { ProjectEditPage } from './pages/ProjectEditPage';
import { FinancingAnalysisPage } from './pages/FinancingAnalysisPage';
import { AdvisorSignupSuccessPage } from './pages/AdvisorSignupSuccessPage';
import { AdvisorSignupForm } from './components/Auth/AdvisorSignupForm';
import { DocumentsPage } from './pages/DocumentsPage';
import { MeetingsPage } from './pages/MeetingsPage';
import { MeetingDetailPage } from './pages/MeetingDetailPage';
import { ProjectDocumentsPage } from './pages/ProjectDocumentsPage';
import { AllDocumentsPreviewPage } from './pages/AllDocumentsPreviewPage';
import { AdvisorManagement } from './components/Admin/AdvisorManagement';
import { CoordinatorDashboardPage } from './pages/CoordinatorDashboardPage';
import { CoordinatorProjectsPage } from './pages/CoordinatorProjectsPage';
import { SetupPage } from './pages/SetupPage';
import { ProfileAnalysisPage } from './pages/ProfileAnalysisPage';
import { AdvisorCalendarPage } from './pages/AdvisorCalendarPage';
import { AdvisorTemplatesPage } from './pages/AdvisorTemplatesPage';
import { AdvisorReportsPage } from './pages/AdvisorReportsPage';
import { AdvisorAlertsPage } from './pages/AdvisorAlertsPage';
import { ResourcesPage } from './pages/ResourcesPage';
import { MessagesPage } from './pages/MessagesPage';
import { AuthCallbackPage } from './pages/AuthCallbackPage';
import { BusinessPlanPage } from './pages/BusinessPlanPage';
import { ProjectOwnersAdminPage } from './pages/ProjectOwnersAdminPage';
import { AdminDashboardPage } from './pages/AdminDashboardPage';
import { AdminUsersPage } from './pages/AdminUsersPage';
import { AuditLogsPage } from './pages/AuditLogsPage';
import ArchivedProjectsPage from './pages/ArchivedProjectsPage';
import { AdvisorRequestsPage } from './pages/AdvisorRequestsPage';
import { ESSDataManagementPage } from './pages/ESSDataManagementPage';
import { RoadmapPage } from './pages/RoadmapPage';
import { RoadmapDetailPage } from './pages/RoadmapDetailPage';
import { FunderProjectsPage } from './pages/FunderProjectsPage';
import { FunderProjectDetailPage } from './pages/FunderProjectDetailPage';
import { useAuthStore } from './store/authStore';
import { Layout } from './components/Layout/Layout';
import { DevelopmentTools } from './utils/developmentTools';

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode; requireProfile?: boolean }> = ({ 
  children, 
  requireProfile = true 
}) => {
  const { isAuthenticated, profileSelected, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requireProfile && !profileSelected) {
    return <Navigate to="/select-profile" replace />;
  }

  return <>{children}</>;
};

function App() {
  const { fetchUser } = useAuthStore();

  useEffect(() => {
    fetchUser();
    
    // Initialize performance monitoring
    PerformanceMonitor.initializeWebVitals();
    
    // Setup development shortcuts
    if (import.meta.env.DEV) {
      DevelopmentTools.setupDevShortcuts();
    }
    
    return () => {
      PerformanceMonitor.cleanup();
    };
  }, []);

  return (
    <ErrorBoundary>
      <ToastProvider>
        <Router>
          <ConnectionStatus />
          <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/reset-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password/update" element={<ResetPasswordPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route path="/advisor-signup" element={<AdvisorSignupForm />} />
        <Route path="/advisor-signup-success" element={<AdvisorSignupSuccessPage />} />
        
        {/* Profile Selection (authenticated but no profile) */}
        {/* Profile Selection removed - users are automatically PORTEUR */}
        
        {/* Protected Routes */}
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          } 
        />
        
        <Route
          path="/projects"
          element={
            <ProtectedRoute>
              <ProjectsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/projects/archived"
          element={
            <ProtectedRoute>
              <ArchivedProjectsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/projects/new"
          element={
            <ProtectedRoute>
              <ProjectWizardIntroPage />
            </ProtectedRoute>
          }
        />
        
        <Route 
          path="/projects/new/step/:stepIndex" 
          element={
            <ProtectedRoute>
              <ProjectWizardStepPage />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/projects/new/documents" 
          element={
            <ProtectedRoute>
              <ProjectWizardDocumentsPage />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/projects/new/review" 
          element={
            <ProtectedRoute>
              <ProjectWizardReviewPage />
            </ProtectedRoute>
          } 
        />
        
        <Route
          path="/projects/:id"
          element={
            <ProtectedRoute>
              <ProjectDetailPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/projects/:id/edit"
          element={
            <ProtectedRoute>
              <ProjectEditPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/projects/:id/financing-analysis"
          element={
            <ProtectedRoute>
              <FinancingAnalysisPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/projects/:id/documents"
          element={
            <ProtectedRoute>
              <ProjectDocumentsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/projects/:id/documents/preview"
          element={
            <ProtectedRoute>
              <AllDocumentsPreviewPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/documents"
          element={
            <ProtectedRoute>
              <DocumentsPage />
            </ProtectedRoute>
          }
        />
        
        <Route 
          path="/coordinator/dashboard" 
          element={
            <ProtectedRoute>
              <CoordinatorDashboardPage />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/coordinator/projects" 
          element={
            <ProtectedRoute>
              <CoordinatorProjectsPage />
            </ProtectedRoute>
          } 
        />
        
        <Route
          path="/admin/accompagnateurs"
          element={
            <ProtectedRoute>
              <Layout><AdvisorManagement /></Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/accompagnateurs/requests"
          element={
            <ProtectedRoute>
              <AdvisorRequestsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/users/porteurs"
          element={
            <ProtectedRoute>
              <ProjectOwnersAdminPage />
            </ProtectedRoute>
          }
        />
        
        <Route 
          path="/rdv" 
          element={
            <ProtectedRoute>
              <MeetingsPage />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/rdv/:id" 
          element={
            <ProtectedRoute>
              <MeetingDetailPage />
            </ProtectedRoute>
          } 
        />
        
        <Route path="/setup" element={
          <ProtectedRoute>
            <SetupPage />
          </ProtectedRoute>
        } />
        
        <Route path="/analysis" element={
          <ProtectedRoute>
            <ProfileAnalysisPage />
          </ProtectedRoute>
        } />
        
        <Route path="/advisor/calendar" element={
          <ProtectedRoute>
            <AdvisorCalendarPage />
          </ProtectedRoute>
        } />
        
        <Route path="/advisor/templates" element={
          <ProtectedRoute>
            <AdvisorTemplatesPage />
          </ProtectedRoute>
        } />
        
        <Route path="/advisor/reports" element={
          <ProtectedRoute>
            <AdvisorReportsPage />
          </ProtectedRoute>
        } />

        <Route path="/admin/dashboard" element={
          <ProtectedRoute>
            <AdminDashboardPage />
          </ProtectedRoute>
        } />

        <Route path="/admin/users" element={
          <ProtectedRoute>
            <AdminUsersPage />
          </ProtectedRoute>
        } />

        <Route path="/admin/audit" element={
          <ProtectedRoute>
            <AuditLogsPage />
          </ProtectedRoute>
        } />

        <Route path="/admin/ess-data" element={
          <ProtectedRoute>
            <ESSDataManagementPage />
          </ProtectedRoute>
        } />

        <Route path="/advisor/alerts" element={
          <ProtectedRoute>
            <AdvisorAlertsPage />
          </ProtectedRoute>
        } />

        <Route path="/roadmap" element={
          <ProtectedRoute>
            <RoadmapPage />
          </ProtectedRoute>
        } />

        <Route path="/roadmap/:projectId" element={
          <ProtectedRoute>
            <RoadmapDetailPage />
          </ProtectedRoute>
        } />

        <Route path="/resources" element={
          <ProtectedRoute>
            <ResourcesPage />
          </ProtectedRoute>
        } />
        
        <Route path="/messages" element={
          <ProtectedRoute>
            <MessagesPage />
          </ProtectedRoute>
        } />
        
        <Route path="/projects/:projectId/business-plan" element={
          <ProtectedRoute>
            <BusinessPlanPage />
          </ProtectedRoute>
        } />

        <Route path="/funder/projects" element={
          <ProtectedRoute>
            <FunderProjectsPage />
          </ProtectedRoute>
        } />

        <Route path="/funder/projects/:id" element={
          <ProtectedRoute>
            <FunderProjectDetailPage />
          </ProtectedRoute>
        } />

        {/* Redirect root to dashboard */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        
        {/* Catch all - redirect to dashboard */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          {import.meta.env.DEV && <DevelopmentPanel />}
        </Router>
      </ToastProvider>
    </ErrorBoundary>
  );
}

export default App;